"use client";

import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";
import { useArtistSession } from "@/components/auth/ArtistSessionProvider";
import { useRouter } from "@/infrastructure/router/navigation";
import {
	beginnerTutorialStore,
	circleDanceTutorial,
	tutorialGuideText,
	tutorialPrompt,
	tutorialText,
	useBeginnerTutorial,
	type CircleDanceTutorialAsset,
} from "@/domain/tutorial/BeginnerTutorial";
import {
	currentAccessToken,
	readArtistSupabaseConfiguration,
} from "@/infrastructure/supabase/client";
import {
	TutorialCoachmark,
	TutorialDimmer,
	TutorialModal,
} from "./BeginnerTutorialUI";

type MediaKind = "image" | "video";

const page: CSSProperties = {
	position: "relative",
	minHeight: "100%",
	background: "#050505",
	color: "var(--ds-on-media-primary)",
	overflow: "hidden",
};

const glass: CSSProperties = {
	background: "color-mix(in srgb, var(--ds-surface) 94%, transparent)",
	backdropFilter: "blur(24px) saturate(150%)",
	WebkitBackdropFilter: "blur(24px) saturate(150%)",
	border: "1px solid var(--ds-divider)",
};

export function CircleDanceTutorialImagine() {
	const tutorial = useBeginnerTutorial();
	const auth = useArtistSession();
	const router = useRouter();
	const [mediaKind, setMediaKind] = useState<MediaKind>("image");
	const [prompt, setPrompt] = useState("");
	const [generating, setGenerating] = useState(false);
	const [selectedAsset, setSelectedAsset] = useState<CircleDanceTutorialAsset>("image");
	const [selectedSlot, setSelectedSlot] = useState(1);
	const [continentsExpanded, setContinentsExpanded] = useState(false);
	const [myAssetsVisible, setMyAssetsVisible] = useState(false);
	const [realStatus, setRealStatus] = useState<string | null>(null);

	const generatedAssets = useMemo(() => {
		const assets: CircleDanceTutorialAsset[] = [];
		if (tutorial.generatedAssetCount >= 1) assets.push("image");
		if (tutorial.generatedAssetCount >= 2) assets.push("circleVideo");
		if (tutorial.generatedAssetCount >= 3) assets.push("closeUpVideo");
		return assets;
	}, [tutorial.generatedAssetCount]);

	const activeMedia = tutorialAsset(selectedAsset);
	const isTutorial = tutorial.step !== "inactive";
	const generationAsset = tutorialGenerationAsset(tutorial.step);

	useEffect(() => {
		if (tutorial.step === "selectImage" && mediaKind === "image") {
			beginnerTutorialStore.advance("selectImage", "focusImagePrompt");
		}
		if (tutorial.step === "selectVideo" && mediaKind === "video") {
			beginnerTutorialStore.advance("selectVideo", "focusCircleVideoPrompt");
		}
	}, [mediaKind, tutorial.step]);

	function focusPrompt() {
		switch (tutorial.step) {
			case "focusImagePrompt":
				setPrompt(tutorialPrompt("image"));
				beginnerTutorialStore.advance("focusImagePrompt", "generateImage");
				break;
			case "focusCircleVideoPrompt":
				setPrompt(tutorialPrompt("video_circle"));
				beginnerTutorialStore.advance("focusCircleVideoPrompt", "generateCircleVideo");
				break;
			case "focusCloseUpVideoPrompt":
				setPrompt(tutorialPrompt("video_close_up"));
				beginnerTutorialStore.advance("focusCloseUpVideoPrompt", "generateCloseUpVideo");
				break;
		}
	}

	function generateTutorialAsset() {
		if (generationAsset === null || generating || prompt.trim() === "") return;
		const price = generationAsset === "image" ? 80 : 520;
		if (!beginnerTutorialStore.debit(price)) return;
		setGenerating(true);
		window.setTimeout(() => {
			beginnerTutorialStore.recordGenerated(generationAsset);
			setSelectedAsset(generationAsset);
			if (generationAsset === "image") beginnerTutorialStore.advance("generateImage", "selectFirstAsset");
			if (generationAsset === "circleVideo") beginnerTutorialStore.advance("generateCircleVideo", "selectSecondAsset");
			if (generationAsset === "closeUpVideo") beginnerTutorialStore.advance("generateCloseUpVideo", "selectThirdAsset");
			setGenerating(false);
		}, 650);
	}

	function selectGeneratedAsset(asset: CircleDanceTutorialAsset) {
		setSelectedAsset(asset);
		if (tutorial.step === "selectFirstAsset" && asset === "image") {
			beginnerTutorialStore.assignSlot(1);
			beginnerTutorialStore.advance("selectFirstAsset", "selectVideo");
		}
		if (tutorial.step === "selectSecondAsset" && asset === "circleVideo") {
			setPrompt("");
			beginnerTutorialStore.advance("selectSecondAsset", "focusCloseUpVideoPrompt");
		}
		if (tutorial.step === "selectThirdAsset" && asset === "closeUpVideo") {
			beginnerTutorialStore.advance("selectThirdAsset", "expandContinents");
		}
	}

	async function generateRealMedia() {
		if (prompt.trim() === "") return;
		if (auth.phase !== "user") {
			router.push("/auth");
			return;
		}
		setGenerating(true);
		setRealStatus(null);
		try {
			const claimedTrackId = await beginnerTutorialStore.claimForRealGeneration();
			const token = await currentAccessToken();
			if (token === null) throw new Error("authentication-required");
			const { url, publishableKey } = readArtistSupabaseConfiguration();
			const isImage = mediaKind === "image";
			const response = await fetch(`${url}/functions/v1/artist-generation/media-generation`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					apikey: publishableKey,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					action: "submit",
					track_id: claimedTrackId,
					provider: isImage ? "fal" : "bytedance",
					model: isImage ? "flux_pro" : "seedance_2_0",
					prompt,
					idempotency_key: crypto.randomUUID(),
					image_count: 1,
					ratio: "9:16",
					duration: isImage ? undefined : 10,
					resolution: isImage ? undefined : "720p",
				}),
			});
			const body = await response.json() as { ok?: boolean; error?: string; job?: { id?: string; status?: string } };
			if (!response.ok || body.ok !== true) throw new Error(body.error ?? "generation-submit-failed");
			setRealStatus(tutorialText("真实生成已进入队列，编辑权已解锁。", "真實生成已進入佇列，編輯權已解鎖。", "Real generation is queued and editing is unlocked."));
		} catch (error) {
			setRealStatus(error instanceof Error ? error.message : "generation-submit-failed");
		} finally {
			setGenerating(false);
		}
	}

	return (
		<main style={page}>
			<div style={{ position: "absolute", inset: 0 }}>
				{activeMedia.type === "video" ? (
					<video
						src={activeMedia.url}
						poster={circleDanceTutorial.assets.cover.public_url}
						autoPlay
						loop
						muted
						playsInline
						style={{ width: "100%", height: "100%", objectFit: "cover" }}
					/>
				) : (
					<img src={activeMedia.url} alt="Circle Dance" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
				)}
				<div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgb(0 0 0 / .34), transparent 36%, rgb(0 0 0 / .72))" }} />
			</div>

			<header style={{ position: "relative", zIndex: 20, display: "flex", alignItems: "center", padding: "max(12px, env(safe-area-inset-top, 0px)) 16px 8px" }}>
				<button type="button" className="ds-button ds-button--secondary" onClick={() => {
					beginnerTutorialStore.exit();
					router.push("/library");
				}}>
					<span aria-hidden="true">×</span>
					<span>{tutorialText("退出教学模式", "退出教學模式", "Exit tutorial mode")}</span>
				</button>
				<span style={{ marginLeft: "auto", color: "var(--ds-accent)", fontWeight: 800 }}>{tutorial.simulatedCredits.toLocaleString()}</span>
				{tutorial.step === "saveComposition" ? (
					<button type="button" className="ds-button ds-button--primary" style={{ marginLeft: 10 }} onClick={() => void beginnerTutorialStore.complete()}>
						{tutorialText("保存", "儲存", "Save")}
					</button>
				) : null}
			</header>

			<section style={{ position: "absolute", zIndex: 20, left: 0, right: 0, bottom: 0, padding: "16px 16px max(24px, env(safe-area-inset-bottom, 0px))", borderRadius: "24px 24px 0 0", color: "var(--ds-text-primary)", ...glass }}>
				{!isTutorial && tutorial.claimedTrackId === null ? (
					<p style={{ marginTop: 0, color: "var(--ds-accent)", fontWeight: 700 }}>
						{tutorialText("生成任意一个真实图片或视频，解锁这首歌的编辑权。", "生成任意一個真實圖片或影片，解鎖這首歌的編輯權。", "Generate any real image or video to unlock editing for this song.")}
					</p>
				) : null}

				{generatedAssets.length > 0 ? (
					<div style={{ display: "flex", gap: 8, marginBottom: 12, overflowX: "auto" }}>
						{generatedAssets.map((asset) => {
							const expected = expectedSelection(tutorial.step);
							const enabled = !isTutorial || expected === asset;
							return (
								<button
									key={asset}
									type="button"
									disabled={!enabled}
									onClick={() => selectGeneratedAsset(asset)}
									style={{ minWidth: 118, padding: 10, borderRadius: 14, border: `1px solid ${enabled ? "var(--ds-accent)" : "var(--ds-divider)"}`, background: "var(--ds-background)", color: "var(--ds-text-primary)", opacity: enabled ? 1 : .42 }}
								>
									{assetLabel(asset)}
								</button>
							);
						})}
					</div>
				) : null}

				<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
					<button type="button" disabled={isTutorial && tutorial.step !== "selectImage"} className={mediaKind === "image" ? "ds-button ds-button--primary" : "ds-button ds-button--secondary"} onClick={() => {
						setMediaKind("image");
						beginnerTutorialStore.advance("selectImage", "focusImagePrompt");
					}}>{tutorialText("图片", "圖片", "Image")}</button>
					<button type="button" disabled={isTutorial && tutorial.step !== "selectVideo"} className={mediaKind === "video" ? "ds-button ds-button--primary" : "ds-button ds-button--secondary"} onClick={() => {
						setMediaKind("video");
						setPrompt("");
						beginnerTutorialStore.advance("selectVideo", "focusCircleVideoPrompt");
					}}>{tutorialText("视频", "影片", "Video")}</button>
					<button type="button" disabled={isTutorial && tutorial.step !== "expandContinents" && !continentsExpanded} className="ds-button ds-button--secondary" onClick={() => {
						setContinentsExpanded(true);
						beginnerTutorialStore.advance("expandContinents", "selectSecondContinent");
					}}>{tutorialText("大陆", "大陸", "Continents")}</button>
				</div>

				{continentsExpanded ? (
					<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
						{[1, 2, 3].map((slot) => {
							const enabled = !isTutorial || (tutorial.step === "selectSecondContinent" && slot === 2) || (tutorial.step === "selectThirdContinent" && slot === 3) || tutorial.assignedSlots.includes(slot);
							return <button key={slot} type="button" disabled={!enabled} className={selectedSlot === slot ? "ds-button ds-button--primary" : "ds-button ds-button--secondary"} onClick={() => {
								setSelectedSlot(slot);
								if (tutorial.step === "selectSecondContinent" && slot === 2) beginnerTutorialStore.advance("selectSecondContinent", "openSecondSlotAssets");
								if (tutorial.step === "selectThirdContinent" && slot === 3) beginnerTutorialStore.advance("selectThirdContinent", "openThirdSlotAssets");
							}}>{slot}{tutorial.assignedSlots.includes(slot) ? " ✓" : ""}</button>;
						})}
					</div>
				) : null}

				{tutorial.step === "openSecondSlotAssets" || tutorial.step === "openThirdSlotAssets" ? (
					<button type="button" className="ds-button ds-button--secondary" style={{ marginBottom: 12 }} onClick={() => {
						setMyAssetsVisible(true);
						if (tutorial.step === "openSecondSlotAssets") beginnerTutorialStore.advance("openSecondSlotAssets", "placeSecondAsset");
						if (tutorial.step === "openThirdSlotAssets") beginnerTutorialStore.advance("openThirdSlotAssets", "placeThirdAsset");
					}}>{tutorialText("更多 · 我的资产", "更多 · 我的資產", "More · My Assets")}</button>
				) : null}

				{myAssetsVisible && (tutorial.step === "placeSecondAsset" || tutorial.step === "placeThirdAsset") ? (
					<button type="button" className="ds-button ds-button--primary" style={{ marginBottom: 12 }} onClick={() => {
						const asset: CircleDanceTutorialAsset = tutorial.step === "placeSecondAsset" ? "circleVideo" : "closeUpVideo";
						setSelectedAsset(asset);
						beginnerTutorialStore.assignSlot(selectedSlot);
						setMyAssetsVisible(false);
						if (tutorial.step === "placeSecondAsset") beginnerTutorialStore.advance("placeSecondAsset", "selectThirdContinent");
						else beginnerTutorialStore.advance("placeThirdAsset", "saveComposition");
					}}>{assetLabel(tutorial.step === "placeSecondAsset" ? "circleVideo" : "closeUpVideo")}</button>
				) : null}

				<textarea
					value={prompt}
					onChange={(event) => setPrompt(event.currentTarget.value)}
					onFocus={focusPrompt}
					placeholder={tutorialText("点按以填入提示词", "點按以填入提示詞", "Tap to insert the prompt")}
					style={{ width: "100%", minHeight: 66, padding: 12, borderRadius: 14, border: "1px solid var(--ds-divider)", background: "var(--ds-background)", color: "var(--ds-text-primary)", resize: "vertical" }}
				/>

				<button
					type="button"
					className="ds-button ds-button--primary"
					style={{ width: "100%", marginTop: 12 }}
					disabled={generating || prompt.trim() === "" || (isTutorial && generationAsset === null)}
					onClick={isTutorial ? generateTutorialAsset : () => void generateRealMedia()}
				>
					{generating ? tutorialText("生成中…", "生成中…", "Generating…") : tutorialText("生成", "生成", "Generate")}
				</button>
				{realStatus ? <p role="status" style={{ color: "var(--ds-accent)" }}>{realStatus}</p> : null}
			</section>

			{isTutorial && tutorial.step !== "imagineIntro" && tutorial.step !== "completion" ? <TutorialDimmer /> : null}
			{tutorial.step === "imagineIntro" ? (
				<TutorialModal
					title={tutorialText("让歌曲更有趣", "讓歌曲更有趣", "Make the song more expressive")}
					text={tutorialGuideText(tutorial.step)}
					actionLabel={tutorialText("继续", "繼續", "Continue")}
					onAction={() => beginnerTutorialStore.advance(
						"imagineIntro",
						mediaKind === "image" ? "focusImagePrompt" : "selectImage",
					)}
				/>
			) : null}
			{tutorial.step === "completion" ? (
				<TutorialModal
					title={tutorialText("新手教学完成", "新手教學完成", "Tutorial complete")}
					text={tutorialGuideText(tutorial.step)}
					actionLabel={tutorialText("完成", "完成", "Done")}
					onAction={() => {
						beginnerTutorialStore.dismissCompletion();
						router.push("/library");
					}}
					icon="✓"
				/>
			) : null}
			{isTutorial && tutorial.step !== "imagineIntro" && tutorial.step !== "completion" ? (
				<TutorialCoachmark step={tutorial.step} text={tutorialGuideText(tutorial.step)} position="top" />
			) : null}
		</main>
	);
}
function tutorialGenerationAsset(step: string): CircleDanceTutorialAsset | null {
	if (step === "generateImage") return "image";
	if (step === "generateCircleVideo") return "circleVideo";
	if (step === "generateCloseUpVideo") return "closeUpVideo";
	return null;
}

function expectedSelection(step: string): CircleDanceTutorialAsset | null {
	if (step === "selectFirstAsset") return "image";
	if (step === "selectSecondAsset") return "circleVideo";
	if (step === "selectThirdAsset") return "closeUpVideo";
	return null;
}

function tutorialAsset(asset: CircleDanceTutorialAsset): { type: "image" | "video"; url: string } {
	if (asset === "image") return { type: "image", url: circleDanceTutorial.assets.image.public_url };
	if (asset === "circleVideo") return { type: "video", url: circleDanceTutorial.assets.circleVideo.public_url };
	return { type: "video", url: circleDanceTutorial.assets.closeUpVideo.public_url };
}

function assetLabel(asset: CircleDanceTutorialAsset): string {
	if (asset === "image") return tutorialText("第一张图片", "第一張圖片", "First image");
	if (asset === "circleVideo") return tutorialText("围圈跳舞视频", "圍圈跳舞影片", "Circle-dance video");
	return tutorialText("树下特写视频", "樹下特寫影片", "Close-up video");
}
